import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const CharacterCounterTool = () => {
  const [text, setText] = useState('');

  const stats = {
    total: text.length,
    withoutSpaces: text.replace(/\s/g, '').length,
    letters: (text.match(/[a-zA-Z]/g) || []).length,
    digits: (text.match(/\d/g) || []).length,
    special: (text.match(/[^a-zA-Z0-9\s]/g) || []).length,
    spaces: (text.match(/\s/g) || []).length,
  };

  const limits = [
    { name: 'Twitter/X', limit: 280, color: stats.total <= 280 ? 'text-emerald-400' : 'text-red-400' },
    { name: 'Instagram Bio', limit: 150, color: stats.total <= 150 ? 'text-emerald-400' : 'text-red-400' },
    { name: 'Facebook Post', limit: 63206, color: stats.total <= 63206 ? 'text-emerald-400' : 'text-red-400' },
    { name: 'LinkedIn Post', limit: 3000, color: stats.total <= 3000 ? 'text-emerald-400' : 'text-red-400' },
    { name: 'SMS', limit: 160, color: stats.total <= 160 ? 'text-emerald-400' : 'text-red-400' },
    { name: 'Meta Title', limit: 60, color: stats.total <= 60 ? 'text-emerald-400' : 'text-red-400' },
    { name: 'Meta Description', limit: 160, color: stats.total <= 160 ? 'text-emerald-400' : 'text-red-400' },
  ];

  const copyCount = () => {
    navigator.clipboard.writeText(stats.total.toString());
    toast.success('Character count copied!');
  };

  return (
    <div className="space-y-6" data-testid="character-counter-tool">
      <Textarea
        placeholder="Type or paste your text here to count characters..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="min-h-[200px] font-mono text-sm bg-muted/50"
      />

      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-4 rounded-xl bg-muted/30 border border-border">
          <h3 className="font-semibold mb-4">Character Breakdown</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Characters</span>
              <span className="font-mono font-semibold text-primary text-lg">{stats.total.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Without Spaces</span>
              <span className="font-mono">{stats.withoutSpaces.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Letters</span>
              <span className="font-mono">{stats.letters.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Digits</span>
              <span className="font-mono">{stats.digits}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Special Characters</span>
              <span className="font-mono">{stats.special}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Spaces</span>
              <span className="font-mono">{stats.spaces}</span>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-muted/30 border border-border">
          <h3 className="font-semibold mb-4">Platform Limits</h3>
          <div className="space-y-2">
            {limits.map((item) => (
              <div key={item.name} className="flex justify-between items-center">
                <span className="text-muted-foreground text-sm">{item.name}</span>
                <span className={`font-mono text-sm ${item.color}`}>
                  {stats.total}/{item.limit}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={copyCount} variant="outline">
          <Copy className="w-4 h-4 mr-2" /> Copy Count
        </Button>
        <Button onClick={() => setText('')} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default CharacterCounterTool;
